import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Label} from '@/components/ui/label/Label'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'
import {Trans, useTranslation} from '@/translations/i18n'
import {
    StyledAgendaEventAccessInfo,
    StyledEventProjectAccessesCountersItem,
    StyledEventProjectAccessesCountersValue,
    StyledMissedAccessesWrapper,
    StyledWarningAccessesReached
} from '@/features/agenda/components/agenda-event-accesses-counters/style'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {useGetPlannedAccessesDetails} from '@/features/planned-accesses/queries/useGetPlannedAccessesDetails'
import {useMemo} from 'react'
import {Text} from '@/components/ui/text/Text'
import {useGetProjectAccessCounters} from '@/features/projects/queries/useGetProjectAccessCounters'
import {EventCategory, EventTypeKeyName} from '@/features/agenda/types'
import dayjs from 'dayjs'
import {Switch} from '@/components/commons/switch/Switch'
import {Controller, useFormContext} from 'react-hook-form'
import {AgendaEventFieldsSchema} from '../../formSchemas'
import {z} from 'zod'
import {AlertTriangleIcon} from '@/components/ui/icon/Icon'
import {eventTypeMapToLabel} from '@/features/agenda/utils'

const FormSchema = AgendaEventFieldsSchema.pick({
    isARecovery: true
})
type FormSchema = z.infer<typeof FormSchema>

export const AgendaEventAccessesCounters = ({
    projectId,
    accessTypeId,
    projectStartDate,
    projectExpirationDate,
    accessType,
    category
}: {
    projectId: string
    accessTypeId: string
    projectStartDate: string | null
    projectExpirationDate: string | null
    accessType: EventTypeKeyName
    category: EventCategory
}) => {
    const {t} = useTranslation()

    const form = useFormContext<FormSchema>()

    const {
        data: plannedAccessCounters,
        isLoading: isPlannedAccessesDetailsLoading,
        isError: isPlannedAccessesDetailsError,
        error: plannedAccessesDetailsError
    } = useGetPlannedAccessesDetails({
        urlParams: {projectId},
        options: {enabled: !!projectId}
    })

    const {
        data: projectAccessCounters,
        isLoading: isProjectAccessCountersLoading,
        isError: isProjectAccessCountersError,
        error: projectAccessCountersError
    } = useGetProjectAccessCounters({
        urlParams: {projectId, accessTypeId},
        options: {enabled: !!projectId}
    })

    const isLoading = useMemo(
        () => isPlannedAccessesDetailsLoading || isProjectAccessCountersLoading,
        [isPlannedAccessesDetailsLoading, isProjectAccessCountersLoading]
    )
    const isError = useMemo(
        () => isPlannedAccessesDetailsError || isProjectAccessCountersError,
        [isPlannedAccessesDetailsError, isProjectAccessCountersError]
    )
    const error = useMemo(
        () => plannedAccessesDetailsError || projectAccessCountersError,
        [plannedAccessesDetailsError, projectAccessCountersError]
    )

    // here we should add an API call to get the counters
    const counters = useMemo(() => {
        const calculateExpectedAccesses = (): number => {
            if (category !== 'treatments' || !projectAccessCounters) {
                return projectAccessCounters?.planned || 0
            }

            const startDate = projectStartDate ? dayjs(projectStartDate) : dayjs()
            const endDate = projectExpirationDate ? dayjs(projectExpirationDate) : dayjs()

            if (projectAccessCounters.frequency === 'weekly') {
                const weeksCount = endDate.diff(startDate, 'week', true)
                return Math.round(weeksCount) * projectAccessCounters.planned
            } else if (projectAccessCounters.frequency === 'monthly') {
                const monthsCount = endDate.diff(startDate, 'month', true)
                return Math.round(monthsCount) * projectAccessCounters.planned
            }

            return projectAccessCounters.planned
        }

        const calculateToBeScheduled = (): number => {
            if (category !== 'treatments' || !projectAccessCounters) {
                return projectAccessCounters?.toBeSchedule || 0
            }

            const remainingAccesses =
                calculateExpectedAccesses() - (projectAccessCounters.scheduled + projectAccessCounters.done)
            return Math.max(remainingAccesses, 0)
        }

        let defaultCounters =
            !isLoading && !isError && projectAccessCounters
                ? [
                      {
                          label: t('accesses:expected'),
                          value: calculateExpectedAccesses()
                      },
                      {
                          label: t('accesses:completed'),
                          value: projectAccessCounters.done
                      },
                      {
                          label: t('accesses:scheduled'),
                          value: projectAccessCounters.scheduled
                      },
                      {
                          label: t('accesses:to_be_scheduled'),
                          value: calculateToBeScheduled()
                      }
                  ]
                : []

        if (category == 'treatments') {
            defaultCounters = defaultCounters.concat({
                label:
                    projectAccessCounters?.frequency == 'monthly'
                        ? t('accesses:monthly_frequency')
                        : t('accesses:weekly_frequency'),
                value:
                    plannedAccessCounters?.treatments.find(item => item.accessType == accessType)?.numberOfAccesses || 0
            })
        }

        return defaultCounters
    }, [
        projectAccessCounters,
        isLoading,
        isError,
        plannedAccessCounters,
        accessType,
        category,
        projectStartDate,
        projectExpirationDate
    ])

    return (
        <Flexbox direction="column" gap={4} fullWidth>
            <Flexbox direction="column" gap={1.5}>
                <Label>{t('agenda:access_info')}</Label>
                <StyledAgendaEventAccessInfo gap={3}>
                    {isLoading ? (
                        Array.from({length: 5}).map((_, index) => (
                            <Flexbox key={index}>
                                <StyledEventProjectAccessesCountersItem>
                                    <Skeleton width={150} height={24} />
                                    <Skeleton width={24} height={24} />
                                </StyledEventProjectAccessesCountersItem>
                            </Flexbox>
                        ))
                    ) : isError ? (
                        error?.response?.status == 404 ? (
                            <Text size="textXs" palette="neutral" shade="500">
                                {t('agenda:no_planned_access_found')}
                            </Text>
                        ) : (
                            <InputHelpText error={t('errors:default')} />
                        )
                    ) : counters.length > 0 ? (
                        counters.map((item, index) => (
                            <StyledEventProjectAccessesCountersItem palette="neutral" shade="600" key={index}>
                                <span>{item.label}</span>
                                <StyledEventProjectAccessesCountersValue>
                                    {item.value}
                                </StyledEventProjectAccessesCountersValue>
                            </StyledEventProjectAccessesCountersItem>
                        ))
                    ) : (
                        <p>{t('agenda:empty_sessions')}</p>
                    )}
                </StyledAgendaEventAccessInfo>
            </Flexbox>
            {projectAccessCounters && (projectAccessCounters.missed > 0 || projectAccessCounters?.withdrawn > 0) && (
                <StyledMissedAccessesWrapper justify="space-between" fullWidth>
                    <StyledEventProjectAccessesCountersItem palette="neutral" shade="600">
                        <span>{t('agenda:missed_accesses')}</span>
                        <StyledEventProjectAccessesCountersValue>
                            {projectAccessCounters?.missed + projectAccessCounters?.withdrawn}
                        </StyledEventProjectAccessesCountersValue>
                    </StyledEventProjectAccessesCountersItem>
                    {category === 'treatments' && (
                        <Controller
                            control={form.control}
                            name="isARecovery"
                            render={({field}) => (
                                <Switch
                                    size="sm"
                                    label={t('agenda:missed_accesses_switch_label')}
                                    checked={!!field.value}
                                    onChange={field.onChange}
                                />
                            )}
                        />
                    )}
                </StyledMissedAccessesWrapper>
            )}

            {!isLoading && !isError ? (
                projectAccessCounters?.done === projectAccessCounters?.planned ? (
                    <StyledWarningAccessesReached align="center" gap={1}>
                        <AlertTriangleIcon size={14} />
                        <Text size="textSm" palette="neutral" shade="950">
                            <Trans
                                i18nKey="agenda:access_all_appointments_done_reached"
                                components={{b: <b />}}
                                values={{
                                    eventType: t(eventTypeMapToLabel[accessType]).toLowerCase()
                                }}
                            />
                        </Text>
                    </StyledWarningAccessesReached>
                ) : counters[0]?.value &&
                  projectAccessCounters?.scheduled &&
                  counters[0].value == projectAccessCounters.scheduled ? (
                    <StyledWarningAccessesReached align="center" gap={1}>
                        <AlertTriangleIcon size={14} />
                        <Text size="textSm" palette="neutral" shade="950">
                            <Trans
                                i18nKey="agenda:access_all_appointments_schedule_reached"
                                components={{b: <b />}}
                                values={{
                                    eventType: t(eventTypeMapToLabel[accessType]).toLowerCase()
                                }}
                            />
                        </Text>
                    </StyledWarningAccessesReached>
                ) : null
            ) : null}
        </Flexbox>
    )
}
